import React, { useState } from 'react'
import {
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material'
import { useCreateValuationPropertyRequest } from '../hooks/useValuationProperty'

export interface SellAHomeWidgetProps {
  embedId: string
  onSubmitSuccess?: () => void
  onSubmitError?: (error: string) => void
}

const SellAHomeWidget: React.FC<SellAHomeWidgetProps> = ({
  embedId,
  onSubmitSuccess,
  onSubmitError
}) => {
  const [formData, setFormData] = useState({
    address: '',
    unit: '',
    email: '',
    propertyType: ''
  })
  const [errors, setErrors] = useState<{
    address?: string
    email?: string
    propertyType?: string
  }>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const createValuationRequest = useCreateValuationPropertyRequest()

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const handleInputChange = (field: 'address' | 'unit' | 'email') => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    // Clear errors when user starts typing
    if (errors[field as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }))
    }
    // Clear success/error messages
    if (submitSuccess) setSubmitSuccess(false)
    if (submitError) setSubmitError(null)
  }

  const handleSelectChange = (event: any) => {
    const value = event.target.value
    setFormData(prev => ({
      ...prev,
      propertyType: value
    }))
    // Clear property type error when user selects
    if (errors.propertyType) {
      setErrors(prev => ({
        ...prev,
        propertyType: undefined
      }))
    }
    // Clear success/error messages
    if (submitSuccess) setSubmitSuccess(false)
    if (submitError) setSubmitError(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validation
    const newErrors: typeof errors = {}
    if (!formData.address.trim()) {
      newErrors.address = 'Address is required'
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email'
    }
    if (!formData.propertyType.trim()) {
      newErrors.propertyType = 'Property type is required'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setIsSubmitting(true)
    setSubmitError(null)
    setSubmitSuccess(false)

    try {
      // Build full address (address + unit if exists)
      const fullAddress = formData.unit.trim()
        ? `${formData.address.trim()}, Unit ${formData.unit.trim()}`
        : formData.address.trim()

      await createValuationRequest.mutateAsync({
        address: fullAddress,
        propertyType: formData.propertyType,
        email: formData.email.trim(),
        embedId: embedId
      })

      // Success
      setSubmitSuccess(true)
      setFormData({
        address: '',
        unit: '',
        email: '',
        propertyType: ''
      })
      onSubmitSuccess?.()
    } catch (error: any) {
      const errorMessage = error.message || 'Error submitting form'
      setSubmitError(errorMessage)
      onSubmitError?.(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        width: '100%',
        p: 0,
        m: 0
      }}
    >
        {/* Address and unit fields - responsive layout */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 0,
            mb: 2,
            border: { xs: 'none', sm: '1px solid #ddd' },
            borderColor: errors.address ? 'error.main' : '#ddd',
            borderRadius: { xs: 0, sm: '12px', md: '10px' },
            overflow: 'hidden',
            minHeight: { xs: '52px', md: '48px' },
            '&:hover': {
              borderColor: { xs: 'transparent', sm: errors.address ? 'error.main' : '#b08b4f' }
            },
            '&:focus-within': {
              borderColor: { xs: 'transparent', sm: errors.address ? 'error.main' : '#b08b4f' },
              borderWidth: { xs: 0, sm: '1px' }
            }
          }}
        >
          <TextField
            fullWidth
            size="small"
            required
            placeholder="Enter Your Home Address"
            value={formData.address}
            onChange={handleInputChange('address')}
            error={!!errors.address}
            helperText={errors.address}
            variant="standard"
            InputProps={{
              disableUnderline: true
            }}
            sx={{
              flex: 1,
              mb: 0,
              '& .MuiInputBase-root': {
                px: { xs: 1.5, md: 1.5 },
                py: { xs: 1.75, md: 1.5 },
                display: 'flex',
                alignItems: 'center',
                height: { xs: '52px', md: '48px' },
                minHeight: { xs: '52px', md: '48px' },
                border: { xs: '1px solid', sm: 'none' },
                borderBottom: { xs: 'none', sm: 'none' },
                borderColor: { xs: errors.address ? 'error.main' : '#ddd', sm: 'transparent' },
                borderRadius: '12px 12px 0 0',
                overflow: 'hidden'
              },
              '& .MuiInputBase-input': {
                fontSize: { xs: '16px', md: '15px' },
                fontFamily: "'Montserrat', sans-serif",
                lineHeight: '1.5',
                padding: 0,
                '&::placeholder': {
                  fontSize: { xs: '16px', md: '15px' },
                  fontFamily: "'Montserrat', sans-serif",
                  opacity: 1
                }
              },
              '& .MuiFormHelperText-root': {
                position: 'absolute',
                top: '100%',
                left: 0,
                mt: 0.5,
                fontSize: '0.875rem',
                fontFamily: "'Montserrat', sans-serif"
              },
              '& .MuiInputBase-root:hover': {
                borderColor: { xs: errors.address ? 'error.main' : '#b08b4f', sm: 'transparent' }
              },
              '& .MuiInputBase-root:focus-within': {
                borderColor: { xs: errors.address ? 'error.main' : '#b08b4f', sm: 'transparent' },
                borderWidth: { xs: '2px', sm: 0 }
              }
            }}
          />
          <Box
            sx={{
              width: { xs: 0, sm: '1px' },
              height: { xs: 0, sm: 'auto' },
              backgroundColor: 'grey.300',
              alignSelf: 'stretch',
              display: { xs: 'none', sm: 'block' }
            }}
          />
          <TextField
            size="small"
            fullWidth
            placeholder="Unit # (optional)"
            value={formData.unit}
            onChange={handleInputChange('unit')}
            variant="standard"
            InputProps={{
              disableUnderline: true
            }}
            sx={{
              width: { xs: '100%', sm: '180px' },
              '& .MuiInputBase-root': {
                px: { xs: 1.5, md: 1.5 },
                py: { xs: 1.75, md: 1.5 },
                display: 'flex',
                alignItems: 'center',
                height: { xs: '52px', md: '48px' },
                minHeight: { xs: '52px', md: '48px' },
                border: { xs: '1px solid', sm: 'none' },
                borderTop: { xs: '1px solid', sm: 'none' },
                borderColor: { xs: '#ddd', sm: 'transparent' },
                borderRadius: '0 0 12px 12px',
                overflow: 'hidden'
              },
              '& .MuiInputBase-input': {
                fontSize: { xs: '16px', md: '15px' },
                fontFamily: "'Montserrat', sans-serif",
                lineHeight: '1.5',
                padding: 0,
                '&::placeholder': {
                  fontSize: { xs: '16px', md: '15px' },
                  fontFamily: "'Montserrat', sans-serif",
                  opacity: 1
                }
              },
              '& .MuiInputBase-root:hover': {
                borderColor: { xs: '#b08b4f', sm: 'transparent' },
                borderTopColor: { xs: '#b08b4f', sm: 'transparent' }
              },
              '& .MuiInputBase-root:focus-within': {
                borderColor: { xs: '#b08b4f', sm: 'transparent' },
                borderTopColor: { xs: '#b08b4f', sm: 'transparent' },
                borderWidth: { xs: '2px', sm: 0 }
              }
            }}
          />
        </Box>

        {/* Email field */}
        <TextField
          fullWidth
          size="small"
          type="email"
          placeholder="Email"
          value={formData.email}
          onChange={handleInputChange('email')}
          error={!!errors.email}
          helperText={errors.email}
          variant="outlined"
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: { xs: '12px', md: '10px' },
              height: { xs: '52px', md: '48px' },
              backgroundColor: 'white',
              fontSize: { xs: '16px', md: '15px' },
              fontFamily: "'Montserrat', sans-serif",
              color: '#0e2340',
              padding: { xs: '14px 16px', md: '12px 16px' },
              '& .MuiInputBase-input': {
                fontSize: { xs: '16px', md: '15px' },
                fontFamily: "'Montserrat', sans-serif",
                '&::placeholder': {
                  fontSize: { xs: '16px', md: '15px' },
                  fontFamily: "'Montserrat', sans-serif",
                  opacity: 1
                }
              },
              '& .MuiFormHelperText-root': {
                fontSize: '0.875rem',
                fontFamily: "'Montserrat', sans-serif"
              },
              '&:hover fieldset': {
                borderColor: errors.email ? 'error.main' : '#b08b4f'
              },
              '&.Mui-focused fieldset': {
                borderColor: errors.email ? 'error.main' : '#b08b4f'
              }
            }
          }}
        />

        {/* Property Type field */}
        <FormControl fullWidth size="small" sx={{ mb: 2 }} error={!!errors.propertyType}>
          <InputLabel 
            id="property-type-label"
            sx={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: { xs: '16px', md: '15px' }
            }}
          >
            Property Type
          </InputLabel>
          <Select
            labelId="property-type-label"
            id="property-type-select"
            value={formData.propertyType}
            label="Property Type"
            onChange={handleSelectChange}
            sx={{
              borderRadius: { xs: '12px', md: '10px' },
              height: { xs: '52px', md: '48px' },
              backgroundColor: 'white',
              fontSize: { xs: '16px', md: '15px' },
              fontFamily: "'Montserrat', sans-serif",
              color: '#0e2340',
              padding: { xs: '14px 16px', md: '12px 16px' },
              paddingRight: '40px',
              '& .MuiSelect-select': {
                fontSize: { xs: '16px', md: '15px' },
                fontFamily: "'Montserrat', sans-serif",
                padding: 0
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: errors.propertyType ? 'error.main' : '#ddd',
                borderWidth: '1px'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: errors.propertyType ? 'error.main' : '#b08b4f'
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: errors.propertyType ? 'error.main' : '#b08b4f',
                borderWidth: '1px'
              }
            }}
          >
            <MenuItem 
              value="Single Family"
              sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: { xs: '16px', md: '15px' } }}
            >
              Single Family
            </MenuItem>
            <MenuItem 
              value="Condo"
              sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: { xs: '16px', md: '15px' } }}
            >
              Condo
            </MenuItem>
            <MenuItem 
              value="Townhouse"
              sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: { xs: '16px', md: '15px' } }}
            >
              Townhouse
            </MenuItem>
            <MenuItem 
              value="Manufactured"
              sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: { xs: '16px', md: '15px' } }}
            >
              Manufactured
            </MenuItem>
            <MenuItem 
              value="Multi-Family"
              sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: { xs: '16px', md: '15px' } }}
            >
              Multi-Family
            </MenuItem>
            <MenuItem 
              value="Apartment"
              sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: { xs: '16px', md: '15px' } }}
            >
              Apartment
            </MenuItem>
            <MenuItem 
              value="Land"
              sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: { xs: '16px', md: '15px' } }}
            >
              Land
            </MenuItem>
          </Select>
          {errors.propertyType && (
            <Box sx={{ 
              color: 'error.main', 
              fontSize: '0.875rem', 
              fontFamily: "'Montserrat', sans-serif",
              mt: 0.5, 
              ml: 1.75 
            }}>
              {errors.propertyType}
            </Box>
          )}
        </FormControl>

        {/* Submit button */}
        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={isSubmitting}
          sx={{
            py: 1.5,
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: 600,
            fontFamily: "'Montserrat', sans-serif",
            textTransform: 'none',
            backgroundColor: '#0e2340',
            '&:hover': {
              backgroundColor: '#b08b4f'
            },
            '&:disabled': {
              backgroundColor: '#e0e0e0',
              color: '#9e9e9e'
            }
          }}
        >
          {isSubmitting ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'Submit'
          )}
        </Button>

        {/* Success/error messages */}
        {submitSuccess && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Your request for a Home Valuation has been submitted successfully. Please look at your INBOX or SPAM to confirm your email address. Once your email is confirmed, you will receive a copy of your valuation.
          </Alert>
        )}

        {submitError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {submitError}
          </Alert>
        )}
    </Box>
  )
}

export default SellAHomeWidget

